import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useAssessmentsKeysQuery, useAssessmentSubmissionsQuery, useFormDefinitionQuery } from '@uplift/queries';
import { CalendarDateService } from '@uplift/services';
import { AssessmentKeys, getAssessmentFormMaxScore } from '@uplift/types';
import { ProgressChart } from '../../../Charts';


const useStyles = makeStyles()(theme => ({
  root: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    paddingTop: theme.spacing(1),
  },
  chartItemColumn: {
    padding: theme.spacing(0, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,

    '&:last-child': {
      borderBottom: 'none',
    }
  }
}));


export type AssessmentsProgressChartWithDetailsProps = {
  patientId: string;
  assessmentId: string;
  assessmentKey: AssessmentKeys;
  onAssessmentDetails?: (assessment: { id: string; key: AssessmentKeys }) => void;
};


export const AssessmentsProgressChartWithDetails: React.VFC<AssessmentsProgressChartWithDetailsProps> = ({
  patientId,
  assessmentId,
  assessmentKey,
  onAssessmentDetails,
}) => {
  const { data: formDefinition } = useFormDefinitionQuery(assessmentId, { suspense: true });
  const { data: assessmentKeys } = useAssessmentsKeysQuery({ suspense: true });
  const { data: submissions } = useAssessmentSubmissionsQuery(patientId, assessmentKey, { suspense: true });

  const maxScore = useMemo(() => getAssessmentFormMaxScore(formDefinition?.formId, assessmentKeys), [ assessmentKeys, formDefinition?.formId ]);

  const chartItems = React.useMemo(() => {
    const completedSubmissions = submissions?.filter(s => s.submission.completedAt);

    return completedSubmissions?.map(submission => ({
      datetime: CalendarDateService.date(submission.submission.createdAt),
      score: submission.score,
      scoreDiffPercentage: submission.scoreDiffPercentage,
    })) ?? [];
  }, [ submissions ]);
  const newestSubmission = React.useMemo(() => chartItems?.[0], [ chartItems ]);

  return (
    <ProgressChart
      title={formDefinition?.name ?? ''}
      data={chartItems}
      maxScore={maxScore}
      summary={newestSubmission}
      fillUpTo={CalendarDateService.date()}
      onDetails={onAssessmentDetails && (() => {
        onAssessmentDetails({ id: assessmentId, key: assessmentKey });
      })}
    />
  );
};


export type AssessmentsProgressChartsProps = {
  patientId: string;
  onAssessmentDetails: AssessmentsProgressChartWithDetailsProps['onAssessmentDetails'];
};


export const AssessmentsProgressCharts: React.FC<AssessmentsProgressChartsProps> = (props) => {
  const { classes } = useStyles();
  const { data } = useAssessmentsKeysQuery({ suspense: true });

  if (!data) {
    return null;
  }

  return (
    <Box className={classes.root}>
      {(Object.keys(data ?? {}) as (keyof typeof data)[]).map(assessmentKey => (
        <Box key={assessmentKey} className={classes.chartItemColumn}>
          <AssessmentsProgressChartWithDetails
            patientId={props.patientId}
            assessmentId={data[assessmentKey]}
            assessmentKey={assessmentKey}
            onAssessmentDetails={props.onAssessmentDetails}
          />
        </Box>
      ))}
    </Box>
  );
};
